import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { AuditLog } from '../types/audit'
import type { ApiError } from '@/types/api'
import axiosInstance from '@/integrations/axios/axios-instance'
import { useAuthStore } from '@/features/auth/store/authStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { usePermissions } from '@/utils/permissions'

export function useAuditLogQuery() {
  const user = useAuthStore((s) => s.user)

  if (!user) {
    throw new Error('User is not authenticated')
  }

  const queryClient = useQueryClient()
  const permissions = usePermissions()
  const queryKey = ['audit-logs', user._id]

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await axiosInstance.get<{ logs: Array<AuditLog> }>(
        'audit/logs',
      )

      return res.data.logs
    },
    enabled: permissions.canAccessAuditLog(),
  })

  useEffect(() => {
    if (query.error) {
      notifyRequestError(query.error as ApiError, 'Failed to fetch audit logs')
    }
  }, [query])

  return { ...query, invalidate }
}
